import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for handling image uploads with drag-and-drop and validation.
 * Handles file selection, preview, validation, and error display.
 *
 * @class ImageUploadController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Error messages for various validation failures.
   * @type {Object}
   */
  errorMessage = {
    'size': 'Invalid image size, must be less then 5MB',
    'count': 'Can not select multiple images',
    'extension': 'Invalid file type, File type must be png/jpeg/gif'
  };

  /**
   * Stimulus targets for DOM elements.
   * @type {string[]}
   */
  static targets = [
    'imageField',
    'currentImage',
    'dragMessage',
    'dragError',
    'dragImage'
  ];

  /**
   * Stimulus values for controller state.
   * @type {Object}
   */
  static values = {
    sourceId: String,
    allowUpload: Boolean
  };


  /**
   * Handles file selection from the file input or drag-and-drop.
   * Shows preview or previous image as appropriate.
   * @param {Event} e
   */
  setImageRecord(e) {
    this.allowUploadValue = true
    this.dragErrorTarget.innerText = ''
    this.sourceIdValue = e.currentTarget.attributes['source-id'].value
    const files = $(`#${this.sourceIdValue}`)[0].files
    if (files.length > 0) {
      this._showImage(files)
      this.imageFieldTarget.files = this._fileListItems(files)
    } else {
      this._showPreviousImage(e.currentTarget)
    }
  }


  /**
   * Handles image drop event for drag-and-drop upload.
   * @param {DragEvent} e
   */
  imageDropped(e) {
    e.preventDefault();
    this.dragErrorTarget.innerText = ''
    if (this.imageFieldTarget.files.length > 0) {
      this.dragErrorTarget.innerText = this.errorMessage['count']
      return
    }
    const files = e.dataTransfer.files
    if (this._checkFile(files)) {
      this.imageFieldTarget.files = this._fileListItems(files)
      this._showImage(files)
    }
  }


  /**
   * Opens the file browser if upload is allowed.
   * Only triggers if allowUploadValue is true.
   */
  browseImage() {
    if (this.allowUploadValue)
      this.imageFieldTarget.click()
  }


  /**
   * Handles file selection from the file input.
   * @param {Event} e
   */
  imageSelected(e) {
    this.dragErrorTarget.innerText = ''
    if (this._checkFile(e.currentTarget.files))
      this._showImage(e.currentTarget.files)
  }


  /**
   * Updates the image field with the selected file and closes the modal.
   * Used after user confirms image selection.
   */
  updateImage() {
    const imageField = document.getElementById(this.sourceIdValue)
    imageField.files = this._fileListItems(this.imageFieldTarget.files)
    this.closeImageModal()
  }


  /**
   * Closes the image modal and resets UI state.
   * Clears all preview and error messages.
   */
  closeImageModal() {
    this.dragErrorTarget.innerText = ''
    $(this.dragImageTarget).html('')
    $(this.currentImageTarget).html('')
    this.imageFieldTarget.value = ''
    $(this.dragMessageTarget).show()
  }


  /**
   * Removes the current image and resets UI state.
   * @param {Event} e
   */
  removeImage(e) {
    this.imageFieldTarget.value = this.dragImageTarget.innerHTML = this.dragErrorTarget.innerText = ''
    $(this.dragMessageTarget, this.dragErrorTarget).show()
    this.allowUploadValue = true
  }


  /**
   * Validates the file(s) for count, extension, and size.
   * @param {FileList} files
   * @returns {boolean}
   */
  _checkFile(files) {
    // All three checks must pass for a valid file
    return (this.checkFilesCount(files) && this.checkFileExtension(files) && this.checkFileSize(files))
  }


  /**
   * Checks that only one file is selected.
   * @param {FileList} files
   * @returns {boolean}
   */
  checkFilesCount(files) {
    const check = files.length == 1
    if (!check)
      this.dragErrorTarget.innerText = this.errorMessage['count']
    return check
  }


  /**
   * Checks that the file size is less than 5MB.
   * @param {FileList} files
   * @returns {boolean}
   */
  checkFileSize(files) {
    const check = (files[0].size / (1024 * 1024)) < 5
    if (!check)
      this.dragErrorTarget.innerText = this.errorMessage['size']
    return check
  }


  /**
   * Checks that the file extension/type is allowed.
   * Only allows PNG, JPG, JPEG, and GIF images.
   * @param {FileList} files
   * @returns {boolean}
   */
  checkFileExtension(files) {
    const check = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'].includes(files[0].type)
    if (!check)
      this.dragErrorTarget.innerText = this.errorMessage['extension']
    return check
  }


  /**
   * Shows the selected image in the preview area.
   * @param {FileList} files
   */
  _showImage(files) {
    const file = files[0]
    // Hide drag message and error, then show preview
    $(this.dragMessageTarget, this.dragErrorTarget).hide()
    $(this.dragImageTarget).html(this._createImageForShow(file))
    this.allowUploadValue = false
  }


  /**
   * Creates the HTML for displaying the image preview.
   * @param {File} file
   * @returns {string}
   */
  _createImageForShow(file) {
    const url = URL.createObjectURL(file);
    // Returns a preview with a remove button
    return `<div class='d-flex flex-column'>
              <img src='${url}' alt='${file.name}' height='100%' />
              <button class='btn btn-danger mt-3' data-action='click->image-upload#removeImage'>remove</button>
            </div>`
  }


  /**
   * Allows drop event for drag-and-drop upload.
   * Prevents default browser behavior.
   * @param {DragEvent} e
   */
  allowDrop(e) {
    e.preventDefault();
  }


  /**
   * Converts a FileList to a DataTransfer file list for assignment to input.
   * This is needed because you can't directly assign a FileList to an input element.
   * Uses DataTransfer API for compatibility.
   * @param {FileList} files
   * @returns {FileList}
   */
  _fileListItems(files) {
    // ClipboardEvent fallback is for some browsers, but DataTransfer is standard
    var obj = new ClipboardEvent("").clipboardData || new DataTransfer()
    for (var i = 0, len = files.length; i < len; i++)
      obj.items.add(files[i])
    return obj.files
  }

  /**
   * Shows the previous image if no new file is selected.
   * Used to restore the preview if the user cancels selection.
   * @param {Element} source
   */
  _showPreviousImage(source) {
    const name = source.attributes['target-image-name']
    const url = source.attributes['target-image-url']
    if (name) {
      // Show previous image and its name if available
      const img = `<img src='${url.value}' alt='${name.value}' height='80px' />`
      const imgTitle = `<p>${name.value}</p>`
      $(this.currentImageTarget).html(img + imgTitle)
    }
  }
}
